import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { EMPTY, of } from 'rxjs';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { FileEntityKey, FileEntityKind } from '../../../../shared/data-types/file-types';
import { HttpParamObject } from '../../../../shared/data-types/http-types';
import { HttpOperation } from '../../../enums/http-operation';
import { FileModel } from '../../../models/file/file.model';
import { ProjectExpenseModel } from '../../../models/finance/expense.model';
import { EndpointService } from '../../../services/endpoints/endpoint.service';
import { HttpErrorService } from '../../../services/http-error.service';
import * as fromFilesActions from '../../files/files.actions';
import * as fromProjectExpensesActions from './project-expenses.actions';

@Injectable()
export class ProjectExpensesEffects {
  fetchProjectExpenses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromProjectExpensesActions.fetchProjectExpenses),
      switchMap(() => {
        const endpoint = this.endpointService.getProjectExpensesSlug(HttpOperation.Get);

        return this.http.get<unknown[]>(endpoint).pipe(
          map((json: unknown[]) => {
            const expenses = json.map((expensePlain) => {
              return plainToInstance(ProjectExpenseModel, expensePlain);
            });
            return fromProjectExpensesActions.setProjectExpenses({ payload: expenses });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Project Expenses',
              fromProjectExpensesActions.setProjectExpenses,
              fromProjectExpensesActions,
              error,
            );
          }),
        );
      }),
    );
  });

  addProjectExpense$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromProjectExpensesActions.addProjectExpense),
      map((action) => action.payload),
      mergeMap((projectExpenseModel) => {
        const endpoint = this.endpointService.getProjectExpensesSlug(HttpOperation.Post);

        const newAttachment = projectExpenseModel.attachment as FileModel;
        const httpExpenseModel = instanceToPlain(projectExpenseModel as ProjectExpenseModel);
        delete (httpExpenseModel as any).id;

        return this.http.post<unknown>(endpoint, httpExpenseModel).pipe(
          map((expense: any) => {
            const newProjectExpenseModel = plainToInstance(ProjectExpenseModel, expense);
            return fromProjectExpensesActions.projectExpenseAdded({
              payload: newProjectExpenseModel,
              newAttachment,
            });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Project Expenses',
              fromProjectExpensesActions.addProjectExpense,
              fromProjectExpensesActions,
              error,
            );
          }),
        );
      }),
    );
  });

  updateProjectExpense$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromProjectExpensesActions.updateProjectExpense),
      map((action) => action.payload),
      mergeMap(({ updatedProjectExpense, oldAttachment }) => {
        const newAttachment = updatedProjectExpense.attachment as FileModel;

        const endpoint = this.endpointService.getProjectExpensesSlug(
          HttpOperation.Put,
          updatedProjectExpense.id,
        );
        return this.http.put(endpoint, instanceToPlain(updatedProjectExpense)).pipe(
          map((json: unknown) => {
            const expense = plainToInstance(ProjectExpenseModel, json);
            return fromProjectExpensesActions.projectExpenseUpdated({
              payload: expense,
              oldAttachment,
              newAttachment,
            });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Project Expenses',
              fromProjectExpensesActions.updateProjectExpense,
              fromProjectExpensesActions,
              error,
            );
          }),
        );
      }),
    );
  });

  deleteProjectExpense$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromProjectExpensesActions.deleteProjectExpense),
      map((action) => action.payload),
      mergeMap((projectExpenseModel) => {
        let requests;
        let endpoint;

        if (Array.isArray(projectExpenseModel)) {
          requests = projectExpenseModel.map((model) => {
            endpoint = this.endpointService.getProjectExpensesSlug(HttpOperation.Delete, model.id);
            return this.http.delete(endpoint, model.id);
          });
        } else {
          endpoint = this.endpointService.getProjectExpensesSlug(
            HttpOperation.Delete,
            projectExpenseModel.id,
          );
          requests = [this.http.delete(endpoint, projectExpenseModel.id)];
        }

        return forkJoin(requests).pipe(
          map(() => {
            return fromProjectExpensesActions.projectExpenseDeleted({
              payload: projectExpenseModel,
            });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Project Expenses',
              fromProjectExpensesActions.deleteProjectExpense,
              fromProjectExpensesActions,
              error,
            );
          }),
        );
      }),
    );
  });

  saveFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        fromProjectExpensesActions.projectExpenseAdded,
        fromProjectExpensesActions.projectExpenseUpdated,
      ),
      mergeMap((action) => {
        const expense = action.payload;

        const toBeUpdatedParams: HttpParamObject = {
          kind: FileEntityKind.Expense,
          [FileEntityKey.ExpenseId]: expense.id,
        };

        // -------- ADDED: just upload
        if (action.type === fromProjectExpensesActions.projectExpenseAdded.type) {
          const fileModel = action.newAttachment ?? null;

          if (!fileModel?.file) {
            return EMPTY;
          }

          return of(
            fromFilesActions.uploadFile({
              payload: { fileModel, params: toBeUpdatedParams },
            }),
          );
        }

        // -------- UPDATED
        const newAttachment = action.newAttachment ?? null;
        const oldAttachment = action.oldAttachment ?? null;

        if (!oldAttachment && !newAttachment) {
          return EMPTY;
        }

        // Updating a file means deleting the old file first and then uploading the new file.
        let toBeDeletedParams: HttpParamObject | null = null;
        if (oldAttachment) {
          toBeDeletedParams = {
            [FileEntityKey.FileId]: oldAttachment.id,
            kind: FileEntityKind.Expense,
          };
        }

        // no new file selected -> nothing to do here
        return of(
          fromFilesActions.updateFile({
            payload: {
              toBeDeletedParams,
              fileModel: newAttachment,
              toBeUpdatedParams,
            },
          }),
        );
      }),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly http: HttpClient,
    private readonly endpointService: EndpointService,
    private readonly httpErrorService: HttpErrorService,
  ) {}
}
